/**
 * Visit-scoped settlement discount adapter. Clock and session persistence stay
 * outside the deterministic sea/economy arithmetic. The existing storage key
 * and 30-minute TTL remain unchanged; this is never soft-coin ↔ paid-Ju exchange.
 */
import { SEA_TRADE_BUY_DISCOUNT_CAP_PCT, seaTradeBuyDiscountPct } from './seaTradeDiscount.ts';

export const SEA_TRADE_BUY_DISCOUNT_STORE_KEY = 'jubuddy.innerSea.tradeBuyDiscount';
/** Destroy-safe backup if a successful buy or leave-pier clear is missed. */
export const SEA_TRADE_BUY_DISCOUNT_TTL_MS = 30 * 60 * 1000;

export type SeaTradeBuyDiscount = {
  readonly pct: number;
  readonly wavesCleared: number;
  readonly grantedAt: number;
};

/** Memory fallback preserves visit scope when session persistence is blocked. */
let memoryGrant: SeaTradeBuyDiscount | null = null;

function readRaw(): SeaTradeBuyDiscount | null {
  if (typeof sessionStorage !== 'undefined') {
    try {
      const raw = sessionStorage.getItem(SEA_TRADE_BUY_DISCOUNT_STORE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<SeaTradeBuyDiscount>;
        const pct = typeof parsed.pct === 'number' ? parsed.pct : 0;
        const wavesCleared = typeof parsed.wavesCleared === 'number' ? parsed.wavesCleared : 0;
        const grantedAt = typeof parsed.grantedAt === 'number' ? parsed.grantedAt : 0;
        if (Number.isFinite(pct) && pct > 0) {
          return {
            pct: Math.min(SEA_TRADE_BUY_DISCOUNT_CAP_PCT, Math.max(0, Math.floor(pct))),
            wavesCleared: Math.max(0, Math.floor(wavesCleared)),
            grantedAt: Number.isFinite(grantedAt) ? grantedAt : 0,
          };
        }
      }
    } catch {
      // fall through to memory
    }
  }
  return memoryGrant && memoryGrant.pct > 0 ? memoryGrant : null;
}

/** Active visit discount, or null when expired / missing. */
export function readSeaTradeBuyDiscount(nowMs = Date.now()): SeaTradeBuyDiscount | null {
  const d = readRaw();
  if (!d) return null;
  if (d.grantedAt > 0 && Number.isFinite(nowMs) && nowMs - d.grantedAt > SEA_TRADE_BUY_DISCOUNT_TTL_MS) {
    clearSeaTradeBuyDiscount();
    return null;
  }
  return d;
}

/** Live pct for quoteBuy (0 when none). */
export function seaBuyDiscountPctNow(nowMs = Date.now()): number {
  return readSeaTradeBuyDiscount(nowMs)?.pct ?? 0;
}

/** Grant on wreck/settle. Overwrites any prior visit discount. */
export function grantSeaTradeBuyDiscount(wavesCleared: number, nowMs = Date.now()): SeaTradeBuyDiscount {
  const pct = seaTradeBuyDiscountPct(wavesCleared);
  const entry: SeaTradeBuyDiscount = {
    pct,
    wavesCleared: Number.isFinite(wavesCleared) ? Math.max(0, Math.floor(wavesCleared)) : 0,
    grantedAt: Number.isFinite(nowMs) ? nowMs : Date.now(),
  };
  memoryGrant = pct > 0 ? entry : null;
  if (typeof sessionStorage !== 'undefined') {
    try {
      if (pct <= 0) {
        sessionStorage.removeItem(SEA_TRADE_BUY_DISCOUNT_STORE_KEY);
      } else {
        sessionStorage.setItem(SEA_TRADE_BUY_DISCOUNT_STORE_KEY, JSON.stringify(entry));
      }
    } catch {
      // Blocked storage: memoryGrant still covers this visit.
    }
  }
  return entry;
}

/** Clear after successful buy or leave-pier. */
export function clearSeaTradeBuyDiscount(): void {
  memoryGrant = null;
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(SEA_TRADE_BUY_DISCOUNT_STORE_KEY);
  } catch {
    // ignore
  }
}
